/**
 * What is about to happen.
 *
 * Four streams that are the same shape and get asked about together: a tenancy about to end, a
 * tenancy whose rent is about to be reviewed, a reservation about to lapse, and an instalment
 * about to fall due. Each is a date in the near future with a name and an amount on it, and each
 * is an appointment somebody has to keep.
 *
 * **The review is the one an agency forgets, which is why it is here rather than on a page of its
 * own.** An expiry announces itself — the tenant is still in the flat and somebody has to decide —
 * while a review is a date in a clause nobody is reminded of, and a review missed is a year let at
 * last year's figure. A tenancy can appear in both tables at once and should: a term ending in
 * eighty days whose rent is reviewed in thirty is two appointments, in that order.
 *
 * **The chart counts; the tables carry the money.** Plotting a count of tenancies and a sum of
 * instalments on one axis would be a two-scale chart, which is the commonest way a dashboard
 * misleads — so the weekly columns are three counts stacked, and each stream's value is given as
 * its own figure above.
 *
 * Each half appears only if the agency holds that module. A letting-only agency is never shown an
 * empty reservations table it has no way to fill, and a development-only one is never asked about
 * tenancies. The server decides which halves it computed at all and says so in `covers`.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { ColumnChart, SERIES } from "@/components/charts";
import { ExpiryBadge } from "@/components/status";
import { Alert, Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, PeriodChoice, ReportHeader, ReportState, usePeriod, useCompactMoney } from "./shared";

interface Pipeline {
  from: string;
  to: string;
  days: number;
  weeks: Array<{ start: string; expiries: number; reviews: number; reservations: number; instalments: number }>;
  expiries: Array<{
    id: string;
    reference: string;
    tenantName: string;
    unitLabel: string;
    propertyName: string;
    endDate: string;
    daysLeft: number;
    bucket: string;
    rentAmount: number;
  }>;
  overdueRenewals: number;
  reviews: Array<{
    id: string;
    reference: string;
    tenantName: string;
    unitLabel: string;
    propertyName: string;
    reviewDate: string;
    daysLeft: number;
    bucket: string;
    rentAmount: number;
  }>;
  overdueReviews: number;
  reservations: Array<{
    id: string;
    reference: string;
    buyerName: string;
    estateName: string;
    plotLabel: string | null;
    expiresOn: string;
    daysLeft: number;
    feeAmount: number;
  }>;
  instalments: Array<{
    id: string;
    purchaseId: string;
    reference: string;
    buyerName: string;
    estateName: string;
    label: string;
    dueDate: string;
    daysLeft: number;
    outstanding: number;
  }>;
  value: { expiries: number; reviews: number; reservations: number; instalments: number };
  covers: { letting: boolean; development: boolean };
}

export function PipelineReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const { vocab } = useSession();
  const [days, setDays] = usePeriod("days", 90);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-pipeline", days],
    queryFn: () => api.get<Pipeline>(`/reports/pipeline?days=${days}`),
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="What is coming" question="What is about to expire, lapse or fall due?" />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);
  const total = data.expiries.length + data.reviews.length + data.reservations.length + data.instalments.length;

  /* The fourth hue, and the last one there is: `SERIES` is four measured colours because a fifth
     would not separate under colour-vision deficiency. Reviews take it rather than reusing a hue
     with a texture, because they sit beside expiries in the same table stack and two letting
     streams that differ only in pattern is a legend nobody reads twice. */
  const streams = [
    data.covers.letting ? { label: `${titleCase(vocab.leases)} ending`, colour: SERIES[1] } : null,
    data.covers.letting ? { label: `${titleCase(vocab.rent)} reviews`, colour: SERIES[0] } : null,
    data.covers.development ? { label: "Reservations lapsing", colour: SERIES[2], texture: "hatch" as const } : null,
    data.covers.development ? { label: "Instalments due", colour: SERIES[3] } : null,
  ].filter(Boolean) as Array<{ label: string; colour: string; texture?: "hatch" }>;

  /*
    Whatever set survives, at least one mark is textured.

    The hatch was fixed to "Reservations lapsing", which is a development stream — so a chart drawn
    with the letting half alone was two hues and no pattern, and told a reader who sees no colour
    nothing at all. That was already true of a letting-only agency and nobody had met it, because
    the suite runs against an agency holding both; narrowing this report to the module in force is
    what made it visible.

    A rule rather than a fourth literal, so a stream added later inherits it. One series is exempt
    and correctly so: there is nothing to tell it apart from, and hatching a lone line makes it
    harder to read.
  */
  if (streams.length > 1 && !streams.some((stream) => stream.texture)) streams[1].texture = "hatch";

  /* The nulls are filtered out in the same order the streams are, so the two arrays stay aligned.
     Adding a stream means adding its row in both places, in the same position. */
  const columns = data.weeks.map((week, index) => ({
    label: `w${index + 1}`,
    full: `Week of ${fmt.date(week.start)}`,
    values: [
      data.covers.letting ? week.expiries : null,
      data.covers.letting ? week.reviews : null,
      data.covers.development ? week.reservations : null,
      data.covers.development ? week.instalments : null,
    ].filter((value): value is number => value !== null),
  }));

  return (
    <div>
      <ReportHeader
        title="What is coming"
        question="Everything with a date on it between today and the end of the horizon."
        subtitle={`${fmt.date(data.from)} to ${fmt.date(data.to)}`}
        actions={
          <PeriodChoice
            label="Ahead"
            value={days}
            onChange={setDays}
            options={[
              { value: 30, label: "30d" },
              { value: 90, label: "90d" },
              { value: 180, label: "180d" },
            ]}
          />
        }
      />

      <div className="space-y-5">
        {data.overdueRenewals > 0 ? (
          <Alert
            tone="danger"
            title={`${data.overdueRenewals} ${data.overdueRenewals === 1 ? `${vocab.lease} has` : `${vocab.leases} have`} already run out`}
          >
            A term that has run out and been neither renewed nor ended is a tenant in occupation on no
            agreement. They are not in the horizon below, because they are already behind it.
          </Alert>
        ) : null}

        {data.overdueReviews > 0 ? (
          <Alert
            tone="warn"
            title={`${data.overdueReviews} ${vocab.rent} ${data.overdueReviews === 1 ? "review is" : "reviews are"} past their date`}
          >
            A review whose day has gone by is {vocab.rent} still being collected at the old figure. They are
            counted here rather than listed below, because a table that always opens on last year's business
            is a table nobody reads.
          </Alert>
        ) : null}

        <FigureRow>
          <Figure label="Appointments" value={total} sub={`In the next ${data.days} days`} />
          {data.covers.letting ? (
            <Figure
              label={`${titleCase(vocab.leases)} ending`}
              value={data.expiries.length}
              tone={data.expiries.length > 0 ? "warn" : "ink"}
              sub={`${compact(data.value.expiries)} of contracted ${vocab.rent}`}
            />
          ) : null}
          {data.covers.letting ? (
            <Figure
              label={`${titleCase(vocab.rent)} reviews`}
              value={data.reviews.length}
              tone={data.reviews.length > 0 ? "warn" : "ink"}
              sub={`${compact(data.value.reviews)} of ${vocab.rent} to be looked at`}
            />
          ) : null}
          {data.covers.development ? (
            <Figure
              label="Reservations lapsing"
              value={data.reservations.length}
              tone={data.reservations.length > 0 ? "warn" : "ink"}
              sub={`${compact(data.value.reservations)} in fees held`}
            />
          ) : null}
          {data.covers.development ? (
            <Figure
              label="Instalments due"
              value={data.instalments.length}
              sub={`${compact(data.value.instalments)} expected`}
            />
          ) : null}
        </FigureRow>

        <ChartCard
          title="Week by week"
          hint="Counts, not amounts — three things of different sizes on one money axis would be a chart that lies. The amounts are in the figures above and in each table below."
        >
          <ColumnChart
            label={`Appointments per week over the next ${data.days} days`}
            mode="stacked"
            highlight={-1}
            height={200}
            points={columns}
            series={streams}
            format={(value) => String(Math.round(value))}
          />
        </ChartCard>

        {data.covers.letting ? (
          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle
                hint="Soonest first. A renewal agreed late is a void nobody planned for."
                action={
                  <Link to="/app/reports/expiries" className="link text-sm">
                    The whole schedule
                  </Link>
                }
              >
                {titleCase(vocab.leases)} ending
              </CardTitle>
            </div>
            {data.expiries.length === 0 ? (
              <EmptyState title={`No ${vocab.lease} ends in this window.`} />
            ) : (
              <Table
                head={
                  <tr>
                    <th>{titleCase(vocab.tenant)}</th>
                    <th>{titleCase(vocab.unit)}</th>
                    <th>Ends</th>
                    <th>When</th>
                    <th className="num">{titleCase(vocab.rent)}</th>
                  </tr>
                }
              >
                {data.expiries.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/app/tenancies/${row.id}`} className="link">
                        {row.tenantName}
                      </Link>
                    </td>
                    <td className="text-ink-500">
                      {row.unitLabel}
                      <span className="block text-xs text-ink-400">{row.propertyName}</span>
                    </td>
                    <td className="text-ink-500">{fmt.date(row.endDate)}</td>
                    <td>
                      <ExpiryBadge endDate={row.endDate} asOf={data.from} />
                      <span className="ml-2 text-xs text-ink-400">in {row.daysLeft}d</span>
                    </td>
                    <td className="num tabular">{full(row.rentAmount)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        ) : null}

        {/*
          Reviews get their own table rather than a column on the expiries one.

          They are a different appointment about a different date, and a tenancy routinely appears
          in both — a term ending in eighty days whose rent is reviewed in thirty. Folding them into
          one table would have to choose which date to sort on, and either choice buries half the
          rows it is supposed to surface.
        */}
        {data.covers.letting ? (
          /* Marked for the verifier, as the portal's tables are: a suite that searched the whole
             page for a name would pass on the expiries table beside this one, which is exactly the
             confusion these two tables exist to avoid. */
          <div data-pipeline-reviews>
          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle hint="Soonest first. A review that passes unnoticed is a year let at the old figure.">
                {titleCase(vocab.rent)} reviews
              </CardTitle>
            </div>
            {data.reviews.length === 0 ? (
              <EmptyState title={`No ${vocab.rent} review falls in this window.`} />
            ) : (
              <Table
                head={
                  <tr>
                    <th>{titleCase(vocab.tenant)}</th>
                    <th>{titleCase(vocab.unit)}</th>
                    <th>Review</th>
                    <th>When</th>
                    <th className="num">{titleCase(vocab.rent)} now</th>
                  </tr>
                }
              >
                {data.reviews.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/app/tenancies/${row.id}`} className="link">
                        {row.tenantName}
                      </Link>
                    </td>
                    <td className="text-ink-500">
                      {row.unitLabel}
                      <span className="block text-xs text-ink-400">{row.propertyName}</span>
                    </td>
                    <td className="text-ink-500">{fmt.date(row.reviewDate)}</td>
                    <td>
                      <ExpiryBadge endDate={row.reviewDate} asOf={data.from} />
                      <span className="ml-2 text-xs text-ink-400">in {row.daysLeft}d</span>
                    </td>
                    <td className="num tabular">{full(row.rentAmount)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
          </div>
        ) : null}

        {data.covers.development ? (
          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle hint="A reservation that lapses puts the node back on the market and, unless the fee was refundable, keeps the fee.">
                Reservations lapsing
              </CardTitle>
            </div>
            {data.reservations.length === 0 ? (
              <EmptyState title="No reservation lapses in this window." />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Reference</th>
                    <th>Buyer</th>
                    <th>Scheme</th>
                    <th>Lapses</th>
                    <th className="num">Fee held</th>
                  </tr>
                }
              >
                {data.reservations.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {/* `/app/reservations/:id` has not existed since the development module moved under its own
                          prefix — it answered "page not found". The address is under Sales, where a
                          reservation is a stage rather than a screen of its own. */}
                      <Link to={`/app/development/sales/reservations/${row.id}`} className="link">
                        {row.reference}
                      </Link>
                    </td>
                    <td>{row.buyerName}</td>
                    <td className="text-ink-500">
                      {row.estateName}
                      {row.plotLabel ? <span className="block text-xs text-ink-400">{row.plotLabel}</span> : null}
                    </td>
                    <td className="text-ink-500">
                      {fmt.date(row.expiresOn)}
                      <span className="ml-2 text-xs text-ink-400">in {row.daysLeft}d</span>
                    </td>
                    <td className="num tabular">{full(row.feeAmount)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        ) : null}

        {data.covers.development ? (
          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle hint="What each buyer owes next, and when. An instalment already settled is not here.">
                Instalments falling due
              </CardTitle>
            </div>
            {data.instalments.length === 0 ? (
              <EmptyState title="Nothing falls due in this window." />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Buyer</th>
                    <th>Sale</th>
                    <th>Instalment</th>
                    <th>Due</th>
                    <th className="num">Outstanding</th>
                  </tr>
                }
              >
                {data.instalments.map((row) => (
                  <tr key={row.id}>
                    <td>{row.buyerName}</td>
                    <td>
                      {/* The sale's own address, rather than the one it had before the module moved — that one
                          still resolves, through a redirect, and a link that needs a redirect to work is a
                          link that will stop working. */}
                      <Link to={`/app/development/sales/${row.purchaseId}`} className="link">
                        {row.reference}
                      </Link>
                      <span className="block text-xs text-ink-400">{row.estateName}</span>
                    </td>
                    <td className="text-ink-500">{row.label}</td>
                    <td className="text-ink-500">
                      {fmt.date(row.dueDate)}
                      <span className="ml-2 text-xs text-ink-400">in {row.daysLeft}d</span>
                    </td>
                    <td className="num tabular font-medium">{full(row.outstanding)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        ) : null}
      </div>
    </div>
  );
}
